import { useEffect, useMemo, useState } from "react";
import * as Icons from "react-icons/bs";
import { TDocument } from "../../types";
import { highlightSearchTextInDocument } from "../../../lib/textutils";
import { newApiService } from "../../services/api-service";
import DocDescription from "./DocDescription";
import DocumentBody from "./DocumentBody";
import { useDocuments } from "../../contexts/DekstopContext";
import LoadingDocument from "../../LoadingDocument";

import '../../scss/document.scss';
import '../../../styles/from-editor/lists.scss';
import '../../../styles/from-editor/document-styles.scss';

const _scrollToSearchIndex = (parent: Element, searchIndex: number) => {
  const elements = parent.getElementsByClassName('lex-highlight')
  const elementAtSearchIndex = elements[searchIndex]
  for (let i = 0; i < elements.length; i++) {
    elements[i].className = elements[i].className.replace(/lex-highlight-active/g, '');
  }
  if (elementAtSearchIndex) {
    elementAtSearchIndex.className = `${elementAtSearchIndex.className} lex-highlight-active`
    elementAtSearchIndex.scrollIntoView({ block: "end", inline: "nearest" })
  }
}

const Document = ({
  windowId,
  docid,
  anchorId,
  docSearchText,
  docSearchCaseSensitive,
  docSearchIndex = 0,
}: {
  windowId: string,
  docid: string,
  anchorId?: string,
  docSearchText?: string,
  docSearchCaseSensitive?: boolean,
  docSearchIndex?: number,
}) => {

  const [doc, setDoc] = useState<TDocument>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { updateWindowTitle, onInnerDocumentSearchResult, windows, toggleShowReds } = useDocuments();
  const showReds = useMemo(() => {
    const w = windows.find((w) => w.id === windowId);
    return w?.showReds !== false;
  }, [windows, windowId]);

  useEffect(() => {
    const fetchDocument = async () => {
      if (!docid) {
        setError('Document ID is missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const doc = await newApiService.loadDocumentById(docid);

        if (doc) {
          setDoc({ ...doc, anchorId });
          setError(null);
          updateWindowTitle(windowId, `${doc.type_name} - ${(doc.title || '').replace(/<\/?[^>]+>/g, '')}`);
        } else {
          setDoc(undefined);
          setError('Document not found');
          updateWindowTitle(windowId, 'New doc');
        }
      } catch (err) {
        console.error('Error fetching doc:', err);
        setError('Failed to load doc');
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [anchorId, docid, updateWindowTitle, windowId]);

  const parsedDocument = useMemo(() => {
    if (!doc) return null;
    const highlighted = highlightSearchTextInDocument(docSearchText, { html: doc.html, title: doc.title }, docSearchCaseSensitive);
    if (!highlighted) {
      return doc
    }
    onInnerDocumentSearchResult(highlighted.found);
    console.log(highlighted.found);
    return { ...doc, ...highlighted };
  }, [doc, docSearchCaseSensitive, docSearchText, onInnerDocumentSearchResult]);

  useEffect(() => {
    if (!parsedDocument || !(parsedDocument as any).found) return

    const windowElement = document.getElementById(windowId);
    if (windowElement) {
      const documentBodyOptions = windowElement.getElementsByClassName('document-body-class-for-reference-only');
      if (documentBodyOptions.length > 0) {
        const parent = documentBodyOptions.item(0);
        if (parent) {
          const elements = parent.getElementsByClassName('lex-highlight')
          if (elements.length) {
            _scrollToSearchIndex(parent, docSearchIndex)
            return
          }
        }

      }
    }

  }, [parsedDocument, docSearchText, docSearchIndex, windowId])

  if (loading) return <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}><LoadingDocument spin={loading} /></div>

  const isModificare = parsedDocument?.revdata?.rev_type === 'Modificare';

  return (
    <div className='doc-wrapper' id={windowId}>
      <div className='doc-meta'>
        {
          parsedDocument
            ? <DocDescription doc={parsedDocument} />
            : null
        }
      </div>
      <div className='doc-show-reds-toggle'>
        <button
          type='button'
          className='doc-show-reds-button'
          onClick={() => toggleShowReds(windowId)}
        >
          {showReds ? <Icons.BsEyeSlash /> : <Icons.BsEye />}
          <span>{showReds ? 'ascunde' : 'arata'} modificarile</span>
        </button>
      </div>
      <div className={`doc-body ${showReds ? '' : 'lex-hide-mods'}`}>
        {
          parsedDocument
            ? <DocumentBody
              loading={false}
              doc={parsedDocument}
              windowid={windowId}
            />
            : !loading && <div className="doc-viewer error">{error || 'Document not found'}</div>
        }
      </div>
    </div>
  )
}

export default Document;
